<script lang="ts">
  import { ACHIEVEMENTS, ACHIEVEMENT_IDS, CLASSES } from '@arena/shared';
  import { click } from '../game/Sfx';
  import { app, send } from '../lib/net.svelte';

  let { onclose }: { onclose: () => void } = $props();

  const TRAILS = ACHIEVEMENT_IDS.filter((id) => ACHIEVEMENTS[id].trail);
  const titles = $derived(
    ACHIEVEMENT_IDS.filter(
      (id) => ACHIEVEMENTS[id].title && id in app.achievements.earned,
    ),
  );
  const unlocked = (id: string) =>
    app.achievements.everyTrail || id in app.achievements.earned;

  function label(id: string): string {
    const a = ACHIEVEMENTS[id];
    const name = CLASSES[a.trail!].name;
    if (unlocked(id)) return name;
    const wins = Math.min(app.achievements.stats[a.stat] ?? 0, a.at);
    return `${name} (${wins.toLocaleString()} / ${a.at} wins)`;
  }

  function onKey(event: KeyboardEvent) {
    if (event.code !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    click();
    onclose();
  }
</script>

<svelte:window onkeydowncapture={onKey} />

{#snippet picker(hand: 'main' | 'off', title: string, chosen: string | null)}
  <label class="row">
    <span class="hand">{title}</span>
    <span
      class="swatch"
      style:background={chosen
        ? CLASSES[ACHIEVEMENTS[chosen].trail!].color
        : ''}
    ></span>
    <select
      value={chosen ?? ''}
      onchange={(e) =>
        send({
          t: 'setTrail',
          hand,
          achievementId: e.currentTarget.value || null,
        })}
    >
      <option value="">None</option>
      {#each TRAILS as id (id)}
        <option value={id} disabled={!unlocked(id)}>{label(id)}</option>
      {/each}
    </select>
  </label>
{/snippet}

<section class="panel character">
  <header>
    <h2>Character</h2>
    <button class="close" onclick={onclose} aria-label="Close">×</button>
  </header>

  <div class="content">
    <h3>Title</h3>
    {#if !titles.length}
      <p class="muted small">
        Earn achievements in rated matches to unlock titles.
      </p>
    {/if}
    <label class="row">
      <span class="hand">Show</span>
      <select
        value={app.achievements.title ?? ''}
        onchange={(e) =>
          send({
            t: 'setTitle',
            achievementId: e.currentTarget.value || null,
          })}
      >
        <option value="">None</option>
        {#each titles as id (id)}
          <option value={id}>{ACHIEVEMENTS[id].title}</option>
        {/each}
      </select>
    </label>

    <h3>Weapon trails</h3>
    <p class="muted small">
      Everyone sees the trails you wear. Win 500 rated matches with a class to
      earn its color.
    </p>
    {@render picker('main', 'Main hand', app.achievements.trail)}
    {@render picker('off', 'Off hand', app.achievements.offTrail)}
  </div>
</section>

<style>
  .character {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(380px, calc(100% - 24px));
    max-height: calc(100% - 48px);
    overflow-y: auto;
    padding: 0;
    pointer-events: auto;
    background: var(--panel-solid);
    border-color: var(--gold);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }
  header {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 10px 18px;
    border-bottom: 1px solid var(--border);
  }
  header h2 {
    margin: 0;
  }
  .close {
    margin-left: auto;
    padding: 0 8px;
    font-size: 18px;
  }
  .content {
    padding: 6px 18px 14px;
  }
  h3 {
    margin: 8px 0 4px;
  }
  p {
    margin: 0 0 10px;
  }
  .row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin: 6px 0;
  }
  .hand {
    width: 72px;
  }
  .swatch {
    width: 28px;
    height: 10px;
    border: 1px solid var(--border);
    border-radius: 5px;
  }
  select {
    flex: 1;
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
  }
</style>
